import { BackWeightData } from '@/library/BackWeightHelper';
import { useEffect, useState } from 'react';
import { TourBagStaticService } from '@/service';
import * as APIModel from '@/api/models';
import { IDropdownOption } from '@/common/EditableDropdown';
import { HandHelper, LoftLieHelper, IronHelper, ShaftLengthHelper, SwingWeightHelper, ShaftHelper } from '@/library';
import { IShaftOptionMap } from '@/library/ShaftHelper';

const roundRibDropdownData: IDropdownOption[] = [
  { id: 'Round', label: 'Round' },
  { id: 'Rib', label: 'Rib' },
];

const logoDropdownData: IDropdownOption[] = [
  { id: 'Up', label: 'Up' },
  { id: 'Down', label: 'Down' },
];

const IRON_HEAD_TYPE_ID = 4;

export interface IIronStaticDropdownData {
  backWeight: BackWeightData;
  ironFamily: IronHelper.IStaticDropdownData<APIModel.Family>;
  ironClub: IronHelper.IStaticDropdownData<APIModel.IronClub>;
  gripSize: IronHelper.IStaticDropdownData<APIModel.GripSize>;
}

export interface IIronDynamicDropdownData {
  ironHeadModel: IronHelper.IDynamicDropdownData<APIModel.HeadModelForDropdownDto>;
}

export interface IUseEditIronResult {
  ironClubData: APIModel.IronClub[] | undefined;
  handDropdownData: HandHelper.IHandOptionMap | undefined;
  loftDropdownData: LoftLieHelper.ILoftOptionMap | undefined;
  lieDropdownData: LoftLieHelper.ILieOptionMap | undefined;
  ironStaticDropdownData: IIronStaticDropdownData | undefined;
  ironDynamicDropdownData: IIronDynamicDropdownData | undefined;
  shaftDropdownData: IShaftOptionMap | undefined;
  shaftLengthDropdownData: ShaftLengthHelper.ShaftLengthOptionMap | undefined;
  swingWtDropdownData: SwingWeightHelper.ISwingWtOptionMap | undefined;
  roundRibDropdownData: IDropdownOption[];
  logoDropdownData: IDropdownOption[];
}

export const useEditIron = () => {
  const [ironClubData, setIronClubData] = useState<APIModel.IronClub[]>();
  const [handDropdownData, setHandDropdownData] = useState<HandHelper.IHandOptionMap>();
  const [loftDropdownData, setLoftDropdownData] = useState<LoftLieHelper.ILoftOptionMap>();
  const [lieDropdownData, setLieDropdownData] = useState<LoftLieHelper.ILieOptionMap>();
  const [ironStaticDropdownData, setIronStaticDropdownData] = useState<IIronStaticDropdownData>();
  const [shaftDropdownData, setShaftDropdownData] = useState<IShaftOptionMap>();
  const [ironDynamicDropdownData, setIronDynamicDropdownData] = useState<IIronDynamicDropdownData>();
  const [shaftLengthDropdownData, setShaftLengthDropdownData] = useState<ShaftLengthHelper.ShaftLengthOptionMap>();
  const [swingWtDropdownData, setSwingWtDropdownData] = useState<SwingWeightHelper.ISwingWtOptionMap>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getDropdownData = async () => {
      const [
        handLookup,
        digitSourceTable,
        fractionSourceTable,
        lieDigitJoinTable,
        lieFractionJoinTable,
        loftDigitJoinTable,
        loftFractionJoinTable,
        ironFamily,
        headTypeFamily,
        ironHeadModel,
        ironShaftName,
        ironClub,
        shaftLengthDigit,
        shaftLengthFraction,
        swingWtPrefix,
        swingWtSuffix,
        gripSize,
        backLocations,
        backNumbers,
        backLocationLookups,
        backNumberLookups,
      ] = await Promise.all([
        TourBagStaticService.getHandLookupData(),
        TourBagStaticService.getDigitData(),
        TourBagStaticService.getFractionData(),
        TourBagStaticService.getLieDigitsData(),
        TourBagStaticService.getLieFractionData(),
        TourBagStaticService.getLoftDigitData(),
        TourBagStaticService.getLoftFractionData(),
        TourBagStaticService.getFamilyData(),
        TourBagStaticService.getHeadTypeFamilyData(IRON_HEAD_TYPE_ID),
        TourBagStaticService.getIronHeadModelsByManufacturerAndTypeForDropdownData(),
        TourBagStaticService.getIronShaftByShaftNameForDropdownData(),
        TourBagStaticService.getIronClubData(),
        TourBagStaticService.getShaftLengthDigitData(),
        TourBagStaticService.getShaftLengthFractionData(),
        TourBagStaticService.getSwingWtPrefixData(),
        TourBagStaticService.getSwingWtSuffixData(),
        TourBagStaticService.getGripSizeData(),
        TourBagStaticService.getHeadWeightLocationBackData(),
        TourBagStaticService.getHeadWeightNumberBackData(),
        TourBagStaticService.getHeadWeightLocationLookupData(),
        TourBagStaticService.getHeadWeightNumberLookupData(),
      ]);

      // set iron club data
      const sortedClubs = ironClub.sort((a, b) => (a?.sortId || 0) - (b?.sortId || 0));
      setIronClubData(sortedClubs);

      // set hand dropdown data
      const handDropdownOptionMap = HandHelper.buildHandDropdown(handLookup);
      setHandDropdownData(handDropdownOptionMap);

      // build loft & lie dropdown data
      const { loftDropdownOptionMap, lieDropdownOptionMap } = LoftLieHelper.buildDropdownData({
        headTypeId: IRON_HEAD_TYPE_ID,
        digitSourceTable,
        fractionSourceTable,
        lieDigitJoinTable,
        lieFractionJoinTable,
        loftDigitJoinTable,
        loftFractionJoinTable,
      });
      setLoftDropdownData(loftDropdownOptionMap);
      setLieDropdownData(lieDropdownOptionMap);

      // build iron static & dynamic dropdown data
      const joainTableFamilyList = headTypeFamily.map((family) => family.familyId); // Create array of family ids for Iron head type
      const headTypeFamilies = ironFamily.filter((family) => joainTableFamilyList.includes(family.familyId)); // Filter families only for Iron head type
      const { ironFamilyDropdownOptionMap, ironClubDropdownOptionMap, gripSizeDropdownOptionMap } = IronHelper.buildStaticDropdownOptionMap({
        ironFamily: headTypeFamilies,
        ironClub,
        gripSize,
      });
      setIronStaticDropdownData({
        backWeight: { locations: backLocations, numbers: backNumbers, locationLookups: backLocationLookups, numberLookups: backNumberLookups },
        ironFamily: ironFamilyDropdownOptionMap,
        ironClub: ironClubDropdownOptionMap,
        gripSize: gripSizeDropdownOptionMap,
      });

      // build manufacturer + shaft name dropdown options
      const shaftNameDropdownOptionMap = ShaftHelper.buildShaftDropdown(ironShaftName);
      setShaftDropdownData(shaftNameDropdownOptionMap);

      const { ironHeadModelDropdownOptionMap } = IronHelper.buildDynamicDropdownOptionMap({ ironHeadModel });
      setIronDynamicDropdownData({
        ironHeadModel: ironHeadModelDropdownOptionMap,
      });

      // build shaft length dropdown data
      const shaftLengthDropdownOptionMap = ShaftLengthHelper.buildDropdownData({
        digitJoinTable: shaftLengthDigit,
        fractionJoinTable: shaftLengthFraction,
        digitSourceTable,
        fractionSourceTable,
      });
      setShaftLengthDropdownData(shaftLengthDropdownOptionMap);

      // build swing wt dropdown data
      const swingWtDropdownOptionMap = SwingWeightHelper.buildDropdownData({ swingWtPrefix, swingWtSuffix });
      setSwingWtDropdownData(swingWtDropdownOptionMap);

      setLoading(false);
    };
    getDropdownData();
  }, []);

  return {
    ironClubData,
    handDropdownData,
    loftDropdownData,
    lieDropdownData,
    ironStaticDropdownData,
    ironDynamicDropdownData,
    shaftDropdownData,
    shaftLengthDropdownData,
    swingWtDropdownData,
    roundRibDropdownData,
    logoDropdownData,
    loading,
  };
};
